'use client';
import React, { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import Link from 'next/link';
import SvgArrow from '@/public/svg/Arrow';
import Socials from '@/app/components/ui-elements/socials';
import Subscribe from '../../sections/Subscribe';
import BlogService from '@/app/services/blog';
import ArticleSkeleton from '@/app/components/ui-elements/skeleton/ArticleSkeleton';
import ArticleError from '@/app/components/ui-elements/error/ArticleError';

export default function PageContent({ params }: { params: any }) {
    const t = useTranslations('News')
    const [data, setData] = useState<any>(null)
    const [error, setError] = useState<string | null>(null)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isExpanded, setIsExpanded] = useState(false);
    const visibleLinks = isExpanded
        ? data?.links
        : data?.links?.slice(0, 3);

    async function getProjectDetails() {
        setIsLoading(true)
        try {
            const result: any = await BlogService.getDetails(params?.slug);
            if (result?.data?.success) {
                    if(result?.data?.data?.length > 0){
                        setData(result?.data?.data[0])
                        setError(null)
                    } else {
                        setData(null)
                        setError(t('article_not_found'))
                    }
                } else {
                    setError(result?.data?.message ?? t('article_not_found'))
                }
        } catch (error: any) {
            setError(error?.response?.data?.message ?? t('article_not_found'))
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => { getProjectDetails() }, [])

    return (
        <>
        {isLoading ? <ArticleSkeleton /> : (
            <>
            {error ? (
                <ArticleError errorMessage={error} displayButton={true}/>
            ) : (
                <div className='mb-10 mt-10 md:mt-20 px-4 md:px-20 text-start'>
            <div className='flex flex-col md:flex-row justify-start items-start md:items-center pb-4'>
                <div className='flex-2'>
                    <h1 className='text-3xl md:text-6xl leading-none text-start font-regular'>
                        {data?.title}
                    </h1>
                    <div className='pb-6 md:pb-16'>
                        <h2 className='text-md md:text-lg leading-normal mt-6'>
                            {data?.description}
                        </h2>
                    </div>
                </div>
                <div className='flex-1 border-l-1 border-slate-100'>
                    <h2 className='text-sm md:text-md leading-normal mt-6 ps-8 uppercase text-gray-300'>
                        {t('date')}
                    </h2>
                    <h2 className='text-sm md:text-md leading-normal mt-1 ps-8 uppercase'>
                        {data?.created_at}
                    </h2>
                    <h2 className='text-sm md:text-md leading-normal mt-6 ps-8 uppercase text-gray-300'>
                        {t('category')}
                    </h2>
                    <h2 className='text-sm md:text-md leading-normal mt-1 ps-8 uppercase'>
                        {data?.categories[0]?.[`name_${params?.locale}`]}
                    </h2>
                </div>
            </div>
            <Image
                src={data?.cover_image?.url}
                width={600}
                height={500}
                alt={`Article ${data?.title}`}
                className="w-full h-auto mt-2 mt-2"
                style={{ objectFit: "cover", width: '72%' }}
                sizes="(max-width: 768px) 80vw, 33vw"
            />
            <div className='flex flex-col md:flex-row flex-wrap justify-start pb-8 pt-4 mt-6'>
                <div className='flex-2 min-w-0 max-w-full'>
                    <div className="px-1 md:px-6">
                        {/* Render HTML Content */}
                        <div
                            className="prose prose-lg prose-violet
                prose-headings:font-bold prose-p:text-gray-700 prose-strong:text-violet-600"
                            style={{overflowWrap: "break-word"}}
                            dangerouslySetInnerHTML={{ __html: data?.content }}
                        />

                        {/* 4. Tags & External Links */}
                        <div className="mt-16 pt-8 border-t border-gray-100">
                            {/* <div className="flex flex-wrap gap-3 mb-8">
                                {data?.tags.map((tag: string, i: number) => (
                                    <div key={i} className="flex items-center gap-1 text-gray-500 bg-gray-100 px-3 py-1 rounded-md text-sm">
                                        {tag}
                                    </div>
                                ))}
                            </div> */}

                            {data?.links.length > 0 && (
                                <div className="space-y-4">
                                    <h3 className="text-lg font-bold flex items-center gap-2">
                                        {/* <LinkIcon size={20} className="text-violet-600" /> */}
                                        {t('related_resources')}
                                    </h3>
                                    {data?.links.map((item: any, i: number) => (
                                        <a
                                            key={i}
                                            href={item.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="block p-4 border border-gray-200 rounded-xl hover:border-violet-400 hover:bg-violet-50 transition-all group"
                                        >
                                            <span className="font-semibold group-hover:text-violet-700">{item.title}</span>
                                            <span className="block text-sm text-gray-400">{item.link}</span>
                                        </a>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className='flex-1 border-l-1 border-slate-100 min-w-0'>
                    <div className='pb-10'>
                        <h2 className='text-sm md:text-md leading-normal mt-6 ps-8 uppercase text-gray-300'>
                            {t('author')}
                        </h2>
                        <h2 className='text-sm md:text-md leading-normal mt-1 ps-8 uppercase'>
                            {data?.author}
                        </h2>
                        {data?.links?.length > 0 && (
                            <>
                                <h2 className='text-sm md:text-md leading-normal mt-6 ps-8 uppercase text-gray-300'>
                                    {t('links')}
                                </h2>
                                <div className="flex flex-col gap-1">
                                    {visibleLinks?.map((lq: any, index: number) => (
                                        <div key={lq.id || index}>
                                            {lq?.link?.length > 0 ? (
                                                <Link
                                                    href={lq.link}
                                                    target='_blank'
                                                    className='block text-sm md:text-md leading-normal ps-8 uppercase text-primary hover:underline'
                                                >
                                                    {lq.title ?? "--"}
                                                </Link>
                                            ) : (
                                                <h2 className='text-sm md:text-md leading-normal ps-8 uppercase'>
                                                    {lq.title ?? "--"}
                                                </h2>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                        {data?.links?.length > 3 && (
                            <button
                                onClick={() => setIsExpanded(!isExpanded)}
                                className="ps-8 mt-2 text-xs font-bold uppercase tracking-widest text-gray-400 cursor-pointer transition-colors flex items-center gap-1"
                            >
                                {isExpanded ? (
                                    <> {t('view_less')} <span className="text-lg">−</span> </>
                                ) : (
                                    <> {t('view_more')} <span className="text-lg">+</span> </>
                                )}
                            </button>
                        )}
                        <h2 className='text-sm md:text-md leading-normal mt-10 ps-8 uppercase text-gray-300'>
                            share
                        </h2>
                        <Socials />
                    </div>
                </div>
            </div>

            {/* <div className="justify-center w-full mt-10 flex gap-6 items-center">
                <h1 className="font-light text-gray-400 uppercase text-lg">{t('next_article')}</h1>
                <div className='rounded-full border p-4'><SvgArrow /></div>
                <Link href='/project'><h1 className="font-light hover:font-medium uppercase text-lg">North Residence News</h1></Link>
            </div> */}
            <Subscribe />
        </div>
            )}
            </>
        )}
        </>
    );
}
